import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, ArrowRight, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import './Login.scss';

interface PlanInfo {
  id: string;
  name: string;
  price: string;
  period: string;
}

const Login: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading, error, clearError } = useAuth();
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [rememberMe, setRememberMe] = useState(false);

  // Get plan information from query params
  const planId = searchParams.get('plan');
  const returnUrl = searchParams.get('return') || (location.state as any)?.from?.pathname;
  
  const planInfo: PlanInfo | null = planId ? {
    id: planId,
    name: planId === 'free' ? 'Starter' : planId === 'pro' ? 'Professional' : 'Enterprise',
    price: planId === 'free' ? 'Free' : planId === 'pro' ? '$25' : '$120',
    period: planId === 'free' ? 'forever' : 'per month'
  } : null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear specific error when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    
    // Clear auth error when user starts typing
    if (error) {
      clearError();
    }
  };

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      await login({
        email: formData.email,
        password: formData.password
      });
      
      // Redirect to appropriate page after successful login
      const redirectUrl = returnUrl || '/dashboard';
      navigate(redirectUrl);
      
    } catch (error) {
      console.error('Login error:', error);
      // Error is handled by the auth context
    }
  };

  return (
    <div className="login">
      <div className="login__container">
        <div className="login__content">
          {/* Left side - Branding */}
          <div className="login__branding-section">
            <div className="login__branding">
              <div className="login__logo">
                <div className="login__logo-icon">
                  <span className="gradient-text">C</span>
                </div>
                <h1 className="login__brand-name">
                  <span className="gradient-text">CoinVAT</span>
                </h1>
              </div>
              
              <h2 className="login__tagline">
                Welcome back to the future of <span className="gradient-text">automated trading</span>
              </h2>
              
              <p className="login__description">
                Continue your journey with AI-powered trading strategies that work 24/7 to maximize your portfolio performance.
              </p>

              <div className="login__stats">
                <div className="login__stat">
                  <strong>$2.5B+</strong>
                  <span>Volume Traded</span>
                </div>
                <div className="login__stat">
                  <strong>50K+</strong>
                  <span>Active Traders</span>
                </div>
                <div className="login__stat">
                  <strong>99.9%</strong>
                  <span>Uptime</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right side - Form */}
          <div className="login__form-section">
            <div className="login__form-container">
              <div className="login__header">
                <h2 className="login__title">Sign In</h2>
                <p className="login__subtitle">
                  Access your CoinVAT trading dashboard
                </p>
              </div>

              {planInfo && (
                <div className="login__plan-info">
                  <div className="login__plan-badge">
                    <AlertCircle size={16} />
                    <span>
                      You'll be subscribed to the <strong>{planInfo.name}</strong> plan 
                      ({planInfo.price}/{planInfo.period}) after signing in
                    </span>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="login__form">
                {error && (
                  <div className="login__error login__error--general">
                    <AlertCircle size={16} />
                    {error}
                  </div>
                )}

                <div className="login__form-group">
                  <label htmlFor="email" className="login__label">
                    <Mail size={16} />
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`login__input ${errors.email ? 'login__input--error' : ''}`}
                    placeholder="Enter your email address"
                    autoComplete="email"
                  />
                  {errors.email && (
                    <span className="login__error">{errors.email}</span>
                  )}
                </div>

                <div className="login__form-group">
                  <label htmlFor="password" className="login__label">
                    <Lock size={16} />
                    Password
                  </label>
                  <div className="login__password-wrapper">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      className={`login__input ${errors.password ? 'login__input--error' : ''}`}
                      placeholder="Enter your password"
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      className="login__password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {errors.password && (
                    <span className="login__error">{errors.password}</span>
                  )}
                </div>

                <div className="login__form-options">
                  <label className="login__checkbox">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                    />
                    <span className="login__checkbox-mark"></span>
                    <span className="login__checkbox-label">Remember me</span>
                  </label>

                  <Link to="/forgot-password" className="login__forgot-link">
                    Forgot password?
                  </Link>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="login__submit btn btn-primary btn-lg"
                >
                  {isLoading ? (
                    <span>Signing In...</span>
                  ) : (
                    <>
                      <span>Sign In</span>
                      <ArrowRight size={16} />
                    </>
                  )}
                </button>

                <div className="login__divider">
                  <span>or</span>
                </div>

                <div className="login__social-login">
                  <button type="button" className="login__social-btn">
                    <svg width="18" height="18" viewBox="0 0 24 24">
                      <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Continue with Google
                  </button>
                </div>
              </form>

              <div className="login__footer">
                <p>
                  Don't have an account?{' '}
                  <Link 
                    to={`/signup${planId ? `?plan=${planId}` : ''}${returnUrl ? `&return=${returnUrl}` : ''}`} 
                    className="login__link"
                  >
                    Create one here
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
